import { Calendar, ChevronRight, Circle, CircleAlert, Clock } from 'lucide-react';
import type { AssignedSummary, QueueFocus } from '../../lib/assigned-queue';
import { cn } from '../../lib/utils';

const CARDS: ReadonlyArray<{
  id: QueueFocus;
  label: string;
  count: (summary: AssignedSummary) => number;
  icon: typeof CircleAlert;
  iconWrap: string;
  iconClass: string;
}> = [
  {
    id: 'urgent',
    label: 'Urgent',
    count: (summary) => summary.urgent,
    icon: CircleAlert,
    iconWrap: 'bg-red-100 dark:bg-red-950/70',
    iconClass: 'text-red-500',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    count: (summary) => summary.overdue,
    icon: Clock,
    iconWrap: 'bg-orange-100 dark:bg-orange-950/70',
    iconClass: 'text-orange-500',
  },
  {
    id: 'this_week',
    label: 'Due this week',
    count: (summary) => summary.dueThisWeek,
    icon: Calendar,
    iconWrap: 'bg-sky-100 dark:bg-sky-950/70',
    iconClass: 'text-sky-500',
  },
  {
    id: 'no_due',
    label: 'No due date',
    count: (summary) => summary.noDueDate,
    icon: Circle,
    iconWrap: 'bg-muted',
    iconClass: 'text-foreground/70',
  },
];

export default function QueueInsightCards({
  summary,
  active,
  onToggle,
}: {
  summary: AssignedSummary;
  active: QueueFocus | null;
  onToggle: (focus: QueueFocus) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const selected = active === card.id;
        return (
          <button
            key={card.id}
            type="button"
            onClick={() => onToggle(card.id)}
            aria-pressed={selected}
            className={cn(
              'flex items-center gap-3 rounded-full border border-black/[0.04] bg-card px-3 py-2 text-left shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-colors hover:bg-muted/40 dark:border-white/10',
              selected && 'ring-1 ring-foreground/20',
            )}
          >
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                card.iconWrap,
              )}
            >
              <Icon className={cn('h-4 w-4', card.iconClass)} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-lg font-semibold tabular-nums leading-none">
                {card.count(summary)}
              </span>
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">{card.label}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
